import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/categories';
import { guideArticles } from '@/mocks/articles';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://kakobuyspreadsheet.cam';

export default function GuideArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = guideArticles.find((a) => a.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  useEffect(() => {
    if (!article) return;

    const articleUrl = `${SITE_URL}/guide/${article.slug}`;

    const articleScript = document.createElement('script');
    articleScript.type = 'application/ld+json';
    articleScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      author: { '@type': 'Organization', name: 'KakobuySpreadsheet', url: SITE_URL },
      datePublished: article.datePublished,
      dateModified: article.dateModified,
      description: article.subtitle,
      publisher: { '@type': 'Organization', name: 'KakobuySpreadsheet', url: SITE_URL },
      url: articleUrl,
      mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
      image: article.heroImage,
    });
    document.head.appendChild(articleScript);

    const faqScript = document.createElement('script');
    faqScript.type = 'application/ld+json';
    faqScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: article.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: { '@type': 'Answer', text: faq.a },
      })),
    });
    document.head.appendChild(faqScript);

    const bcScript = document.createElement('script');
    bcScript.type = 'application/ld+json';
    bcScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'KakobuySpreadsheet', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: article.category, item: `${SITE_URL}/#categories` },
        { '@type': 'ListItem', position: 3, name: article.title, item: articleUrl },
      ],
    });
    document.head.appendChild(bcScript);

    return () => {
      if (articleScript.parentNode) document.head.removeChild(articleScript);
      if (faqScript.parentNode) document.head.removeChild(faqScript);
      if (bcScript.parentNode) document.head.removeChild(bcScript);
    };
  }, [article]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center bg-background-50 pt-20">
          <div className="text-center px-6">
            <h1 className="font-heading text-3xl font-bold text-foreground-900 mb-4">Article Not Found</h1>
            <p className="text-foreground-500 mb-6">The guide you are looking for does not exist.</p>
            <Link
              to="/"
              className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        <section className="relative w-full min-h-[55vh] flex items-center justify-center overflow-hidden">
          <img
            src={article.heroImage}
            alt={article.title}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/50"></div>
          <div className="relative z-10 text-center px-6 max-w-3xl mx-auto pt-20">
            <span className="inline-block px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 text-white/90 text-xs font-semibold mb-4">
              {article.category}
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
              {article.title}
            </h1>
            <p className="text-white/75 text-sm md:text-base mt-5 max-w-2xl mx-auto leading-relaxed">
              {article.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8">
              <Link
                to="/"
                className="whitespace-nowrap px-6 py-3 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white text-sm font-medium hover:bg-white/30 transition-all duration-200 cursor-pointer"
              >
                &larr; Back to Home
              </Link>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </section>

        <article className="py-16 md:py-20 bg-background-50">
          <div className="max-w-3xl mx-auto px-6">
            <div className="text-foreground-800 leading-relaxed text-sm md:text-base">
              {article.sections.map((section, si) => (
                <div key={si}>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mt-12 first:mt-0 mb-4">
                    {section.heading}
                  </h2>
                  {section.paragraphs.map((p, pi) => (
                    <p
                      key={pi}
                      className="text-foreground-600 leading-relaxed mb-4"
                      dangerouslySetInnerHTML={{ __html: p }}
                    />
                  ))}
                  {section.listItems && section.listItems.length > 0 && (
                    <ul className="space-y-2.5 mb-6 ml-1">
                      {section.listItems.map((item, li) => (
                        <li key={li} className="flex gap-3 text-sm text-foreground-600 leading-relaxed">
                          <span className="w-5 h-5 flex-shrink-0 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs font-bold mt-0.5">
                            {li + 1}
                          </span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}

              {article.faqs.length > 0 && (
                <div className="mt-14">
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6">
                    Frequently Asked Questions
                  </h2>
                  <div className="space-y-3">
                    {article.faqs.map((faq, fi) => (
                      <div
                        key={fi}
                        className="rounded-xl bg-background-100 border border-background-200/70 p-5"
                      >
                        <h3 className="font-heading font-semibold text-sm text-foreground-900 mb-2">
                          {faq.q}
                        </h3>
                        <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-14 p-8 rounded-2xl bg-background-100 border border-background-200/70 text-center">
                <h3 className="font-heading text-xl font-bold text-foreground-900 mb-3">
                  Ready to Start Shopping?
                </h3>
                <p className="text-sm text-foreground-500 mb-5 max-w-md mx-auto leading-relaxed">
                  Browse verified seller links and make safer buying decisions with <strong>KakobuySpreadsheet</strong>.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link
                    to="/"
                    className="whitespace-nowrap px-6 py-3 rounded-full border border-foreground-300 text-sm font-medium text-foreground-700 hover:bg-foreground-900 hover:text-background-50 hover:border-foreground-900 transition-all duration-200 cursor-pointer"
                  >
                    &larr; Back to Home
                  </Link>
                  <a
                    href={mainSiteUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="whitespace-nowrap px-6 py-3 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer"
                  >
                    Buy It Now
                  </a>
                </div>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}